import { useMemo } from 'react';
import { QueryConfig, QueryResult, useQuery } from 'react-query';
import { RefDataNAICSSectorApi, NaicsSector, NaicsSubSector } from 'eve-account-client';
import { titleCase } from 'title-case';
import { useConfiguration } from './BaseApi';

export const useRefDataNAICSSectorApi = (): RefDataNAICSSectorApi => {
  const configuration = useConfiguration();
  const api = useMemo(() => new RefDataNAICSSectorApi(configuration), [configuration]);
  return api;
};

const getNaicsSectors = async (api: RefDataNAICSSectorApi): Promise<NaicsSector[]> => {
  const request = api.getNaicsSectors({});

  // TODO: Format on the backend or in the database.
  return (await request).map(
    (naicsSector): NaicsSector => ({
      ...naicsSector,
      displayName: titleCase(naicsSector.displayName.toLowerCase()),
    }),
  );
};

export const useNaicsSectorsQuery = (): QueryResult<NaicsSector[], Error | Response> => {
  const api = useRefDataNAICSSectorApi();

  return useQuery<NaicsSector[], Error | Response>(['naicsSectors'], () => getNaicsSectors(api));
};

const getNaicsSubSectors = async (naicsSectorCode: string, api: RefDataNAICSSectorApi): Promise<NaicsSubSector[]> => {
  const request = api.getNaicsSubSectors({ naicsSector: naicsSectorCode });

  // TODO: Format on the backend or in the database.
  return (await request).map(
    (naicsSubSector): NaicsSubSector => ({
      ...naicsSubSector,
      displayName: titleCase(naicsSubSector.displayName.toLowerCase()),
    }),
  );
};

export const useNaicsSubSectorsQuery = (naicsSectorCode?: string): QueryResult<NaicsSubSector[], Error | Response> => {
  const api = useRefDataNAICSSectorApi();

  return useQuery<NaicsSubSector[], Error | Response>(
    ['naicsSubSectors', naicsSectorCode],
    () => getNaicsSubSectors(naicsSectorCode as string, api),
    {
      enabled: Boolean(naicsSectorCode),
    },
  );
};

const getNaicsSubSectorsById = async (
  naicsSubSectorCode: string,
  api: RefDataNAICSSectorApi,
): Promise<NaicsSubSector> => {
  const request = api.getNaicsSubSectorsById({ naicsSubSector: naicsSubSectorCode });
  const naicsSubSector = await request;

  // TODO: Format on the backend or in the database.
  return {
    ...naicsSubSector,
    displayName: titleCase(naicsSubSector.displayName.toLowerCase()),
  };
};

export const useNaicsSubSectorByIdQuery = (
  naicsSubSectorCode?: string,
  queryOptions?: QueryConfig<NaicsSubSector, Error | Response>,
): QueryResult<NaicsSubSector, Error | Response> => {
  const api = useRefDataNAICSSectorApi();

  return useQuery<NaicsSubSector, Error | Response>(
    ['naicsSubSector', naicsSubSectorCode],
    () => getNaicsSubSectorsById(naicsSubSectorCode as string, api),
    {
      ...queryOptions,
      enabled: Boolean(naicsSubSectorCode) && queryOptions?.enabled !== false,
    },
  );
};
